import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { GlobalData, GlobalState } from './globalReducerTypes';
import { rollupReducers } from './rollupReducers';
import { automationReducers } from './automationReducers';
import type { RootState } from '../index';
import { assignmentReducers } from './assignmentReducers';
import { alertsReducers } from './alertsReducers';
import { dedupMatchingReducers } from './dedupMatchingReducers';
import { scheduledAssignmentReducers } from './scheduledAssignmentReducers';
import { scheduledReportReducers } from './scheduledReportReducers';
import { scheduledJobReducers } from './scheduledJobReducers';
import { marketingAttributionReducer } from './marketingAttributionReducer';
import { parserReducer } from './parserReducer';

const initialState: GlobalState = {
  environments: {},
  loadingStatuses: {},
};

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setGlobalData: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        globalData: GlobalData;
      }>,
    ) => {
      const { crmOrgId, globalData } = action.payload;

      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = {
          data: globalData,
          ui: {
            refreshVersion: 0,
          },
        };
        state.loadingStatuses[crmOrgId] = {
          ...state.loadingStatuses[crmOrgId],
          isLoading: undefined,
          error: undefined,
        };

        return;
      }

      // Note: we intentionally do NOT reset refreshVersion here.
      // The listener uses a ref to track which version was last fetched,
      // so if refreshVersion advanced during this fetch, it will re-fetch.
      state.environments[crmOrgId].data = globalData;
      state.loadingStatuses[crmOrgId] = {
        ...state.loadingStatuses[crmOrgId],
        isLoading: undefined,
        error: undefined,
      };
    },
    requestRefreshGlobal: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      if (!state.environments[crmOrgId]?.ui) {
        state.environments[crmOrgId] = {
          ui: {
            refreshVersion: 1,
          },
        };
        return;
      }
      state.environments[crmOrgId].ui.refreshVersion =
        (state.environments[crmOrgId].ui.refreshVersion || 0) + 1;
    },
    setGlobalIsLoading: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      state.loadingStatuses[crmOrgId] = state.loadingStatuses[crmOrgId] || {};
      state.loadingStatuses[crmOrgId].isLoading = true;
      state.loadingStatuses[crmOrgId].error = undefined;
    },
    setGlobalError: (state, action: PayloadAction<{ crmOrgId: string; error: any }>) => {
      const { crmOrgId, error } = action.payload;
      state.loadingStatuses[crmOrgId] = state.loadingStatuses[crmOrgId] || {};
      state.loadingStatuses[crmOrgId].isLoading = false;
      state.loadingStatuses[crmOrgId].error = error;
    },
    ...parserReducer,
    ...rollupReducers,
    ...automationReducers,
    ...assignmentReducers,
    ...alertsReducers,
    ...dedupMatchingReducers,
    ...scheduledAssignmentReducers,
    ...scheduledReportReducers,
    ...scheduledJobReducers,
    ...marketingAttributionReducer,
  },
});

export const selectLoadingStatuses = (state: RootState) => state.global.loadingStatuses;

export const selectRefreshVersion = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.ui.refreshVersion ?? 0;

export const selectEnvironments = (state: RootState) => state.global.environments;

export const selectElementDependencies = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.elementDependencies;

export const selectOrgSweepFields = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.sweepFields;

export const selectFieldByFieldId = (crmOrgId: string, fieldId?: string) => (state: RootState) => {
  if (!fieldId) return undefined;
  return state.global.environments[crmOrgId]?.data?.sweepFields[fieldId];
};

export const {
  setGlobalData,
  requestRefreshGlobal,
  setGlobalIsLoading,
  setGlobalError,

  addRollup,
  updateRollup,
  deleteRollup,
  updateDeployStatus,

  addAutomation,
  updateAutomation,
  toggleAutomation,
  deleteAutomation,
  updateAutomationDeployStatus,

  addAssignment,
  updateAssignment,
  toggleAssignment,
  deleteAssignment,
  updateAssignmentDeployStatus,

  addAlert,
  updateAlert,
  toggleAlert,
  deleteAlert,
  updateAlertDeployStatus,

  addDedupMatching,
  updateDedupMatching,
  toggleDedupMatching,
  deleteDedupMatching,
  updateDedupMatchingDeployStatus,

  addScheduledAssignment,
  updateScheduledAssignment,
  toggleScheduledAssignment,
  deleteScheduledAssignment,
  updateScheduledAssignmentDeployStatus,

  addScheduledReport,
  updateScheduledReport,
  toggleScheduledReport,
  deleteScheduledReport,
  updateScheduledReportDeployStatus,

  addScheduledJob,
  updateScheduledJob,
  toggleScheduledJob,
  deleteScheduledJob,
  updateScheduledJobDeployStatus,

  addMarketingAttributionModel,
  updateMarketingAttributionModel,
  toggleMarketingAttributionModel,
  deleteMarketingAttributionModel,
  updateMarketingAttributionModelDeployStatus,
  setMarketingAttributionModels,

  addParsedDataToDocumentation,
} = globalSlice.actions;

export default globalSlice.reducer;
